// Page Signup Team Component

import React, { Component } from 'react'
import PropTypes from 'prop-types'

import app from '../../../app.jsx'
import SignupTeamContent from '../../signup/signup_teamcontent/signup_teamcontent.jsx'
import DateUtil from '../../../lib/dateutil.jsx'
import Alert from '../../common/alert/alert.jsx'
import { TeamExistsError } from '../../../../shared/errors/TeamExistsError'
import analyticsModule from '../../../library/analytics/index'
import { ANALYTICS_EVENTS, ANALYTICS_FIELDS } from '../../../library/analytics/constants'
import { SUBSCRIPTION_TYPES, SUPPORT_ACCOUNTS, LANGUAGE_BY_COUNTRY } from '../../../../shared/constants.js'
class PageSignupTeam extends Component {
  static get propTypes () {
    return {
      country: PropTypes.string,
      email: PropTypes.string
    }
  }

  static get defaultProps () {
    return {
      country: '',
      email: ''
    }
  }

  constructor (props) {
    super(props)

    this.state = {
      dataDate: null
    }

    this.data = {
      userTeam: null
    }

    this._enableFbq = false
    this._references = {
      signupContent: React.createRef()
    }

    const config = app.getConfig()
    if (config && config.fbq) {
      this._enableFbq = !!config.fbq.enabled
    }
  }

  render () {
    return (
      <div className='page-signup'>
        {this._renderSignupContent()}
        <Alert />
      </div>
    )
  }

  _renderSignupContent () {
    const recaptchaEnabled = app.config.recaptcha.enabled
    return (
      <SignupTeamContent
        ref={this._references.signupContent}
        recaptchaEnabled={recaptchaEnabled}
        country={this.props.country}
        checkUserExists={this._checkUserExists.bind(this)}
        onSubmit={this._onSignupTeam.bind(this)}
        email={this.props.email}
      />
    )
  }

  /* Private functions */
  async _checkUserExists (email, callback) {
    if (email) {
      const userParams = { query: { email: (email || '').toLowerCase() } }
      try {
        const userRes = await app.service('/api/users').find(userParams)

        if (userRes && userRes.data && userRes.data.length > 0) {
          callback(true) // eslint-disable-line standard/no-callback-literal
        } else {
          callback(false) // eslint-disable-line standard/no-callback-literal
        }
      } catch (error) {
        app.debug('### PageSignupTeam: _checkUserExists ', error)
        callback(false) // eslint-disable-line standard/no-callback-literal
      }
    } else {
      callback(false) // eslint-disable-line standard/no-callback-literal
    }
  }

  async checkTeamExists ({ teamName }) {
    const teamFindResults = await app.service('/api/team').find({
      query: {
        name: teamName
      }
    })

    return teamFindResults.total > 0
  }

  async _onSignupTeam (data, userExists) {
    const { email, password, companyName, firstName, lastName } = data
    let user, team

    if (this._references.signupContent.current) {
      this._references.signupContent.current.hideError()
    }

    try {
      if (userExists) {
        // TODO: checkTeamExists validation is only implemented on frontend
        // in ROD-1152, we will implement this on backend
        if (await this.checkTeamExists({ teamName: companyName })) {
          throw new TeamExistsError()
        }
        user = (await app.authentication.login({ email, password })).user
        team = await this._signupTeam(data, true)
      } else {
        team = await this._signupTeam(data, false)
        user = (await app.authentication.login({ email, password })).user
      }

      await this._addArchivedSupportUser(team.id, data.country)

      analyticsModule.getMixpanelInstance({ team, user }).track(ANALYTICS_EVENTS.signUp, {
        [ANALYTICS_FIELDS.name]: `${firstName} ${lastName}`,
        [ANALYTICS_FIELDS.email]: email,
        [ANALYTICS_FIELDS.company]: companyName,
        [ANALYTICS_FIELDS.type]: SUBSCRIPTION_TYPES.free // pre-optimization for in case we have other "premium" types in the future
      })

      await app.teamContext.switchToTeamById({ teamId: team.id, userId: user?.id })
      await this._createUserHistory(user?.id, team.id, userExists)
    } catch (error) {
      console.error(error)

      if (!this._references.signupContent.current) return undefined

      if (error.translationKey) {
        return this._references.signupContent.current.showError(error.translationKey)
      }

      if (error.message === 'Team with same name already exists') {
        return this._references.signupContent.current.showError(
          'user.signupTeam.msgErrTeamAlreadyExists'
        )
      }

      if (error.message === 'User with same email already exists') {
        return this._references.signupContent.current.showError(
          'user.signupTeam.msgErrUserAlreadyExists'
        )
      }

      return this._references.signupContent.current.showError(
        'user.signupTeam.msgErrInCreatingTeam'
      )
    }
  }

  async _signupTeam (signupObj, bUserExists) {
    const signupParams = {
      query: {
        clientTimezoneOffset: DateUtil.getLocalTimezoneOffset(),
        $client: {
          userExists: {
            value: bUserExists
          }
        }
      }
    }

    signupObj.timezone = this._getTimezone(signupObj.country)
    app.service('/api/page/signup').timeout = 40000

    const response = await app.service('/api/page/signup').create(signupObj, signupParams)

    this.data.userTeam = response.userTeam
    this._callFacebookPixel()

    return response.team
  }

  _getTimezone (country) {
    if (country === 'DE') {
      return 'Europe/Berlin'
    } else if (country === 'UK') {
      return 'Europe/London'
    } else if (country === 'US') {
      return 'America/New_York'
    }

    return 'Europe/Amsterdam'
  }

  _callFacebookPixel () {
    if (!this._enableFbq) {
      return false
    }

    let tries = 1
    this._loadFbq()
    this._fbqScriptTimer = window.setInterval(() => {
      const fbq = window.fbq
      if (fbq || tries > 10) {
        window.clearInterval(this._fbqScriptTimer)

        if (fbq) {
          fbq('init', '784623004996468')
          fbq('track', 'PageView')
          fbq('track', 'CompleteRegistration')
        }
      }

      tries++
    }, 2000)
  }

  async _createUserHistory (userId, teamId, bUserExists) {
    const params = {
      query: {
        $client: { teamId: teamId }
      }
    }

    try {
      const result = await app.service('/api/user/history').create({ userId }, params)

      if (result) {
        app.set('appUserHistoryId', result.id)
      }

      if (!bUserExists) {
        app.redirect('/emailconfirm')
      } else {
        app.redirect('/')
      }
    } catch (err) {
      app.debug('### PageSignupTeam: _createUserHistory ', err)

      if (!bUserExists) {
        app.redirect('/emailconfirm')
      } else {
        app.redirect('/')
      }
    }
  }

  async _addArchivedSupportUser (teamId, country) {
    const email = SUPPORT_ACCOUNTS[country]
    const user = await app.service('/api/users').find({ query: { email } })
    const createData = {
      userId: user.data[0].id,
      teamId: teamId,
      email: email,
      status: 'archived',
      roleType: 'Full user',
      roles: ['Account owner', 'Project Management', 'Finance'],
      firstName: 'Support',
      lastName: 'Rodeo',
      language: LANGUAGE_BY_COUNTRY[country],
      functions: 'tech support',
      rateCards: [{ rate: 1, costRate: 1 }],
      timezone: this._getTimezone(country)
    }

    const params = {
      query: {
        $client: {
          sendInvite: {
            value: false
          }
        }
      }
    }

    app
      .service('/api/users/team')
      .create(createData, params)
      .catch(error => {
        console.log(error)
        throw error
      })
  }

  /* eslint-disable lines-between-class-members, no-unused-expressions */
  _loadFbq () {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments)
      }
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = !0
      n.version = '2.0'
      n.queue = []
      t = b.createElement(e)
      t.async = !0
      t.src = v
      s = b.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script', '//connect.facebook.net/en_US/fbevents.js')
  }
  /* eslint-enable no-unused-expressions */
}

export default PageSignupTeam
